/**
 * Purchase order contracts (S3.1 rewrite) — Board D #3, SO→PO.
 *
 * Fixed decisions encoded here (holistic plan §S3.1):
 * - `poNumber` = `PO-` + sequence, auto-assigned, never enterable. The exact
 *   numbering rule is CONFIGURABLE (pending Joe — appendix B): the default
 *   `PoNumberRule` embeds YYYYMM before the sequence (PO-202607-0004) to
 *   match the seeded narrative; flipping `includeYearMonth` yields a flat
 *   global sequence without a schema change.
 * - `sourceSoId`: 1 SO → 1..N PO. The SO↔PO line join itself is the
 *   first-class `Allocation` row (`./allocation`), NOT an FK here.
 * - `vendorId` REQUIRED (US-266) with preferred-vendor prefill;
 *   `vendorPrefillSource === 'preferred'` renders the 主要供應商 source tag.
 * - Lines: {productId, qty, purchasePrice auto from the product's vendor
 *   line unitCost, expectedDate}.
 * - Status chain: draft → sent → vendor_confirmed → shipped → received
 *   (草稿/已送出/供應商已確認/已出貨/已進貨). Approval is a SEPARATE state:
 *   totals ≥ NT$200k flip `approvalStatus` to 'pending' — blocking SEND,
 *   not draft entry; a manager releases via ReasonRequiredModal
 *   (actor + reason mandatory).
 * - 檢查第二次 second-check aids ride on every create: open-PO count,
 *   lowest-price hint over the last 3 purchases, duplicate-history warning
 *   (same product+vendor open PO).
 * - Shortage gate: SO confirm reads the mocked NetSuite availability
 *   endpoint (read-through, visible `syncedAt`); the quantity is ALWAYS
 *   labeled 可供量 (Available Quantity) in business copy. Shortage lines
 *   pre-fill the PO with the shortage qty only.
 */

import type { Allocation } from './allocation';

// Barrel passthrough: `src/types/api/index.ts` is outside the S3.1 file set
// (concurrent slices own it this wave) — re-export the new allocation
// contracts here so `@/types/api` consumers resolve today. Follow-up: move
// this line into the barrel proper.
export * from './allocation';

// ---------------------------------------------------------------------------
// Status + approval state
// ---------------------------------------------------------------------------

/** 草稿 / 已送出 / 供應商已確認 / 已出貨 / 已進貨 */
export type PurchaseOrderStatus =
  | 'draft'
  | 'sent'
  | 'vendor_confirmed'
  | 'shipped'
  | 'received';

export const PURCHASE_ORDER_STATUS_ORDER: readonly PurchaseOrderStatus[] = [
  'draft',
  'sent',
  'vendor_confirmed',
  'shipped',
  'received',
] as const;

/**
 * Approval is a parallel state, not a status: 'pending' blocks the
 * draft→sent transition ONLY (the PO stays editable as a draft).
 */
export type PurchaseOrderApprovalStatus = 'not_required' | 'pending' | 'approved' | 'rejected';

/** >NT$200k (TWD-converted total) requires manager release before send. */
export const PO_APPROVAL_THRESHOLD_TWD = 200000;

/** ReasonRequiredModal contract — actor + reason are both mandatory. */
export interface PurchaseOrderApproval {
  decision: 'approve' | 'reject';
  decidedBy: string;
  reason: string;
  decidedAt: string;
}

// ---------------------------------------------------------------------------
// PO numbering (rule configurable — pending Joe)
// ---------------------------------------------------------------------------

export interface PoNumberRule {
  /** Always 'PO-' for now; kept as data so the rule can change without code. */
  prefix: string;
  /** true → PO-YYYYMM-seq (per-month sequence); false → PO-seq (global). */
  includeYearMonth: boolean;
  seqPadding: number;
}

export const DEFAULT_PO_NUMBER_RULE: PoNumberRule = {
  prefix: 'PO-',
  includeYearMonth: true,
  seqPadding: 4,
};

// ---------------------------------------------------------------------------
// 檢查第二次 second-check aids
// ---------------------------------------------------------------------------

export interface PoSecondCheckPriceRow {
  poNumber: string;
  orderDate: string;
  vendorId: number;
  vendorName: string;
  purchasePrice: number;
  currency: string;
}

export type PoSecondCheckWarning = 'above_lowest_recent' | 'duplicate_open_order';

/**
 * The 檢查第二次 aids surfaced at PO creation (and on demand via
 * GET /purchase-orders/second-check) so the buyer double-checks BEFORE
 * committing — the anti-duplicate-order artifact.
 */
export interface PoLineSecondCheck {
  /** Open (not yet received) POs containing this product — any vendor. */
  openPoCount: number;
  /** Last 3 purchases of this product (any vendor), newest first. */
  recentPrices: PoSecondCheckPriceRow[];
  /** Lowest of `recentPrices` — the lowest-price hint. Null when no history. */
  lowestRecentPrice: number | null;
  /** Σ open qty of this product on OPEN POs to the SAME vendor. */
  duplicateOpenQty: number;
  warnings: PoSecondCheckWarning[];
}

// ---------------------------------------------------------------------------
// Lines + header
// ---------------------------------------------------------------------------

export interface PurchaseOrderLine {
  id: string;
  productId: string;
  /** Display mirror of the product SKU at creation time. */
  sku: string;
  /** Display mirror of the product display name at creation time. */
  productName: string;
  qty: number;
  /** Auto-filled from the vendor line's negotiated unitCost when omitted. */
  purchasePrice: number;
  /**
   * @deprecated required read-only mirror of `purchasePrice`, kept populated
   * for pre-S3.1 consumers (S1.4 transactions grid reads `line.unitCost`);
   * dies once useProducts.ts migrates to `purchasePrice`.
   */
  unitCost: number;
  currency: string;
  /** 'vendor_line' auto-prefill · 'manual' explicit entry on create. */
  priceSource: 'vendor_line' | 'manual';
  /** Per-line expected date (plan §S3.1 line field). */
  expectedDate: string | null;
  /** 檢查第二次 snapshot taken at creation; null on pre-S3.1 seeds. */
  secondCheck: PoLineSecondCheck | null;
  /**
   * Enriched on detail reads from the first-class join (never stored on the
   * line — /allocations is the source of truth).
   */
  allocations?: Allocation[];
}

export interface PurchaseOrder {
  id: string;
  /** Auto-assigned per `PoNumberRule`, never enterable. */
  poNumber: string;
  /** 1 SO → 1..N PO; null for POs not raised from a single SO. */
  sourceSoId: string | null;
  /** Display mirror for header chips. */
  sourceSoNumber?: string | null;
  /** REQUIRED (US-266) — prefilled from the preferred vendor when possible. */
  vendorId: number;
  vendorName: string;
  /** 'preferred' renders the 主要供應商 tag; 'so'/'manual' render plain. */
  vendorPrefillSource: 'preferred' | 'manual' | 'so' | null;
  status: PurchaseOrderStatus;
  orderDate: string;
  currency: string;
  lines: PurchaseOrderLine[];
  /** Σ qty × purchasePrice, 2dp. */
  totalAmount: number;
  /** TWD-converted total ≥ PO_APPROVAL_THRESHOLD_TWD at creation. */
  approvalRequired: boolean;
  approvalStatus: PurchaseOrderApprovalStatus;
  /** Set on manager release/reject (ReasonRequiredModal). */
  approval: PurchaseOrderApproval | null;
  /** A1 estimate acknowledgement (S3.2 layers A1/A2/A3 checkpoints on top). */
  vendorAcknowledgedAt: string | null;
  vendorConfirmationNote: string | null;
  paymentTerms: string | null;
  /** Header-level fallback when lines carry no per-line expectedDate. */
  expectedDeliveryDate: string | null;
  createdAt: string;
  updatedAt: string;
}

// ---------------------------------------------------------------------------
// Requests / responses
// ---------------------------------------------------------------------------

export interface PurchaseOrderListParams {
  page?: number;
  pageSize?: number;
  /** Matches poNumber, vendorName and sourceSoNumber. */
  search?: string;
  status?: PurchaseOrderStatus;
  vendorId?: number;
  approvalStatus?: PurchaseOrderApprovalStatus;
}

export interface PurchaseOrderListResponse {
  data: PurchaseOrder[];
  pagination: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
}

export interface CreatePurchaseOrderAllocationInput {
  soLineId: string;
  qty: number;
}

export interface CreatePurchaseOrderLineInput {
  productId: string;
  qty: number;
  /** Omit to auto-fill from the vendor line's negotiated unitCost. */
  purchasePrice?: number;
  /** @deprecated legacy alias of `purchasePrice` — codex-era create form. */
  unitCost?: number;
  expectedDate?: string;
  /** Direct-mode convenience: one SO line covered in full by this line. */
  soLineId?: string;
  /**
   * Explicit allocations. One entry → mode 'direct'; several → 'aggregate'.
   * Σ qty MUST equal the line qty (ALLOCATION_QTY_MISMATCH otherwise).
   */
  allocations?: CreatePurchaseOrderAllocationInput[];
}

export interface CreatePurchaseOrderRequest {
  vendorId: number;
  currency?: string;
  sourceSoId?: string;
  paymentTerms?: string;
  expectedDeliveryDate?: string;
  lines: CreatePurchaseOrderLineInput[];
}

/** ReasonRequiredModal payload for the >NT$200k release. */
export interface PurchaseOrderDecisionRequest {
  decision: 'approve' | 'reject';
  actor: string;
  reason: string;
}

/** US-266 preferred-vendor prefill (served by /products/{id}/vendor-prefill). */
export interface VendorPrefillResponse {
  productId: string;
  preferredVendorId: number | null;
  preferredVendorName: string | null;
  alternatives: Array<{
    vendorId: number;
    vendorName: string;
    unitCost: number;
    isPreferred: boolean;
  }>;
}

// ---------------------------------------------------------------------------
// NetSuite availability read-through + shortage gate (Board 7)
// ---------------------------------------------------------------------------

/**
 * One mocked NetSuite availability row (read-through v2 — WR0 SR0.1).
 * `availableQty` is ALWAYS surfaced as 可供量 (Available Quantity) in business
 * copy — see `ofcVocabulary.availableQuantity`; the read-through `syncedAt` is
 * always visible next to it.
 *
 * v2 extends the row with Tier-1 echoes that exist purely as RECONCILIATION
 * REFERENCE (NetSuite 參考值), never as formula inputs — the reorder engine's
 * on-order term is the Tier-2 derived `qtyOpenToReceive` (plan Appendix D-1).
 * Adding these fields is contract-additive: the shortage gate + SO-create
 * availability chip read `availableQty` only and are unchanged.
 */
export interface NetSuiteAvailabilityRow {
  productId: string;
  /** Tier-1 read-only external mapping (Item 品項 is API-surface-only). */
  netSuiteItemId: string | null;
  availableQty: number;
  /**
   * NEW v2 — Tier-1 open-PO on-order echo (NetSuite `item.QuantityOnOrder`).
   * `null` = not synced. Reconciliation reference only (a |delta| vs the
   * Tier-2 `qtyOpenToReceive` downgrades suggestion confidence — never the
   * engine input). Whether NetSuite's value includes synced ERP POs is OQ-7.
   */
  qtyOnOrder: number | null;
  /** NEW v2 — Tier-1 Preferred Stock Level echo (reconciliation display only). */
  preferredStockLevel?: number | null;
  /** NEW v2 — Tier-1 Reorder Point echo (reconciliation display only). */
  reorderPoint?: number | null;
  syncedAt: string;
}

export interface NetSuiteAvailabilityResponse {
  data: NetSuiteAvailabilityRow[];
  syncedAt: string;
  /**
   * NEW v2 — delta-sync watermark (SuiteQL `LastQuantityAvailableChange`
   * pattern): the newest Tier-1 availability-change moment covered by this
   * read-through. Distinct from `syncedAt` (the read moment) so a consumer
   * can delta-poll "items changed since X". Always ≤ `syncedAt`.
   */
  lastAvailabilityChange?: string;
}

/**
 * One row of the per-location availability grid (Item Locations, DD!179 Must)
 * — a Tier-1 NetSuite read-through echoed per 地點 (location). Read-only: the
 * ERP web never owns on-hand, so these numbers do NOT drill into any Tier-2
 * page. `syncedAt` rides every row.
 *
 * `inTransit` (在途調撥, in-transit transfer) is carried in the contract for
 * Joe but its UI COLUMN is HELD pending the owner ruling OQ-18: inter-location
 * transfer/movement language on a Tier-2 surface that deliberately has no
 * warehouse surfaces is a boundary/vocab call even as a read-only echo.
 * On Hand / Committed / Avg Cost / Total Value columns are DEFERRED (OQ-6).
 */
export interface NetSuiteItemLocationRow {
  /** 地點 — AU location name (Tier-1 read-only echo; a proper noun, not chrome). */
  location: string;
  /** 可供量 — per-location Available Quantity. */
  availableQty: number;
  /** 在途採購 — per-location open-PO on-order (Tier-1 echo). */
  qtyOnOrder: number;
  /** 在途調撥 — in-transit transfer. UI column HELD pending OQ-18. */
  inTransit: number;
  /** 欠交 — back-ordered (ordered, nothing to commit). */
  backOrdered: number;
  syncedAt: string;
}

/**
 * Server-computed totals across all location rows (Σ). Carries every numeric
 * bucket but no `location` — the UI supplies the bold totals-row label.
 */
export type NetSuiteItemLocationTotals = Omit<NetSuiteItemLocationRow, 'location'>;

export interface NetSuiteItemLocationsResponse {
  productId: string;
  /** Tier-1 read-only external mapping; `null` when the product is unmapped. */
  netSuiteItemId: string | null;
  rows: NetSuiteItemLocationRow[];
  /** Σ over `rows`; `totals.availableQty` === the header `availableQty`. */
  totals: NetSuiteItemLocationTotals;
  syncedAt: string;
  /** Delta-sync watermark — same contract as the aggregate response. */
  lastAvailabilityChange?: string;
}

/**
 * One Item-Locations row + the DD!75–78 On Hand columns (S1.1 extension,
 * promoted here from `mocks/handlers/netsuite.ts` by Y-S0 — the recorded
 * followUp; the handler now imports these so mock and app share ONE source).
 * Still a read-only Tier-1 echo: zero write affordances, the ERP web never
 * owns on-hand, and every number is SERVER-computed (engine purity — no
 * arithmetic in components).
 */
export interface NetSuiteItemLocationOnHandRow extends NetSuiteItemLocationRow {
  /** 現有量 (DD!75) — availableQty + committed (Tier-1 echo, read-only). */
  qtyOnHand: number;
  /** 基本單位現有量 (DD!76) — qtyOnHand × stock-unit→base-unit factor. */
  qtyOnHandBaseUnit: number;
  /** 帳面價值 (DD!77) — qtyOnHand × averageCost, 2dp. */
  value: number;
  /** 平均成本 (DD!78) — per-location moving average, 2dp. */
  averageCost: number;
}

/**
 * Server-computed totals across all location rows. `averageCost` is the
 * VALUE-WEIGHTED average (Σvalue / ΣqtyOnHand), not a naive column mean; 0
 * when nothing is on hand (the UI renders dashes for empty read-throughs —
 * the zero-vs-empty gate, never a fabricated cost of 0.00).
 */
export type NetSuiteItemLocationOnHandTotals = Omit<NetSuiteItemLocationOnHandRow, 'location'>;

/** The extended `/netsuite/availability/:productId/locations` payload. */
export interface NetSuiteItemLocationsOnHandResponse extends NetSuiteItemLocationsResponse {
  rows: NetSuiteItemLocationOnHandRow[];
  totals: NetSuiteItemLocationOnHandTotals;
}

/** Per-line shortage verdict — 是否有短缺 at SO confirm. */
export interface ShortageCheckLine {
  soLineId: string;
  productId: string;
  sku: string;
  productName: string;
  qtyOrdered: number;
  /** 可供量 at syncedAt (after sequential draw-down across earlier lines). */
  availableQty: number;
  /** max(0, qtyOrdered − availableQty) — pre-fills the PO qty. */
  shortageQty: number;
}

export interface ShortageCheckResponse {
  soId: string;
  syncedAt: string;
  hasShortage: boolean;
  lines: ShortageCheckLine[];
}
